/**
 * Phone parsing for the click-to-call surface.
 *
 * `parsePhone` produces an E.164 string for the `tel:` href and a
 * human-readable display string for the link text. Default country is
 * Canada (Autoflo's launch market); if CA parse fails we try US so
 * border / snowbird numbers still link cleanly. If both fail we hand
 * back the raw input plus `isValid: false` so the caller can fall
 * back to plain text instead of producing a broken `tel:` link.
 *
 * Extensions parse out: an input like "(416) 555-1234 ext 3" yields
 * `e164 = "+14165551234;ext=3"` (RFC 3966 syntax that iOS and Android
 * dialers respect) and `display = "(416) 555-1234 ext. 3"`.
 *
 * Kept separate from `src/lib/format.ts#formatPhone` so the linkable
 * surface doesn't disturb the existing locale-naive display helper —
 * that one stays the canonical *display-only* path for cases where
 * the parent is itself clickable and a nested anchor would conflict.
 */

import { parsePhoneNumberWithError } from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'

export type ParsedPhone = {
  /** E.164 with optional `;ext=N` suffix when an extension was found. */
  e164: string
  /** Human-readable display string. National format for NANP numbers. */
  display: string
  /** True when libphonenumber accepted the input as a valid number. */
  isValid: boolean
}

const FALLBACK_COUNTRIES: CountryCode[] = ['US']

/**
 * Parse a raw phone string into href + display + validity.
 *
 * Returns `isValid: false` (and `e164 = ''`) for null/empty/unparseable
 * input. Display always carries something printable — the raw input
 * for unparseable values, an em-dash for null/empty.
 */
export function parsePhone(
  raw: string | null | undefined,
  country: CountryCode = 'CA',
): ParsedPhone {
  if (!raw || !raw.trim()) {
    return { e164: '', display: '—', isValid: false }
  }
  const trimmed = raw.trim()

  for (const c of [country, ...FALLBACK_COUNTRIES]) {
    try {
      const parsed = parsePhoneNumberWithError(trimmed, c)
      if (!parsed || !parsed.isValid()) continue
      const base = parsed.format('NATIONAL') // "(416) 555-1234"
      const display = parsed.ext ? `${base} ext. ${parsed.ext}` : base
      const e164 = parsed.ext
        ? `${parsed.number};ext=${parsed.ext}`
        : parsed.number
      return { e164, display, isValid: true }
    } catch {
      // Try next country.
    }
  }
  return { e164: '', display: trimmed, isValid: false }
}

/** Strip everything but ASCII digits. Used by the search-by-phone path. */
export function digitsOnly(input: string | null | undefined): string {
  if (!input) return ''
  return input.replace(/\D+/g, '')
}

/**
 * Substring-match a phone-shaped query against a stored phone string.
 * Both sides are reduced to digits-only; we then check whether the
 * query digits appear anywhere in the stored digits. Handles every
 * common typing pattern — full E.164 paste, last-7 local, area-code-
 * only typing (e.g. "416"), partial prefix as the user is still
 * typing, formatted input with parens / dashes / spaces — with one
 * rule. Returns false when the query has no digits or the stored
 * value is empty, so it's safe to OR into a substring filter that
 * already covers non-digit queries (name, etc.).
 */
export function phoneMatches(
  query: string | null | undefined,
  stored: string | null | undefined,
): boolean {
  const q = digitsOnly(query)
  const s = digitsOnly(stored)
  if (!q || !s) return false
  return s.includes(q)
}
